import React, { useEffect } from "react";
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAnnouncements, Announcement } from "@/hooks/useAnnouncements";
import moment from "moment";

interface AnnouncementsPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onOpenAnnouncement: (announcement: Announcement) => void;
}

const getCategoryColorProps = (category: string) => {
  switch (category) {
    case "FEATURE":
    case "UPDATE":
    case "PROMOTION":
      return { bgcolor: "#e0f2fe", color: "#0284c7" }; // Light blue, dark blue text
    case "MAINTENANCE":
      return { bgcolor: "#fef3c7", color: "#d97706" }; // Light amber, dark amber text
    case "GENERAL":
      return { bgcolor: "#dcfce7", color: "#16a34a" }; // Light green, dark green
    case "OUTAGE":
      return { bgcolor: "#fee2e2", color: "#dc2626" }; // Light red, dark red
    default:
      return { bgcolor: "#f3f4f6", color: "#4b5563" }; // Gray
  }
};

export const AnnouncementsPopover: React.FC<AnnouncementsPopoverProps> = ({
  anchorEl,
  onClose,
  onOpenAnnouncement,
}) => {
  const theme = useTheme();
  const open = Boolean(anchorEl);
  const { announcements, loading, fetchAnnouncements } = useAnnouncements();

  useEffect(() => {
    if (open) {
      // Fetch only active announcements for the user
      fetchAnnouncements({ status: "ACTIVE", limit: 10 });
    }
  }, [open, fetchAnnouncements]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            maxHeight: 500,
            borderRadius: 3,
            mt: 1.5,
            boxShadow: theme.shadows[10],
            backgroundColor: "background.paper",
            backdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" fontWeight="600">
          Зарлалууд
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ color: "#4f46e5", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          onClick={onClose}
        >
          Бүгдийг уншсан болгох
        </Typography>
      </Box>
      <Divider />
      
      {loading && announcements.length === 0 ? (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : announcements.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Шинэ зарлал алга байна
          </Typography>
        </Box>
      ) : (
        <List 
          sx={{ 
            p: 0, 
            flexGrow: 1, 
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
              borderRadius: 3,
              "&:hover": {
                backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
              },
            },
          }}
        >
          {announcements.map((announcement) => {
            const colors = getCategoryColorProps(announcement.category || "GENERAL");
            return (
              <React.Fragment key={announcement.id}>
                <ListItem
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    p: 2.5,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                  onClick={() => {
                    onOpenAnnouncement(announcement);
                    onClose();
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Chip 
                      label={announcement.category || "GENERAL"} 
                      size="small" 
                      sx={{ 
                        bgcolor: colors.bgcolor, 
                        color: colors.color, 
                        fontWeight: 800, 
                        fontSize: "0.65rem", 
                        height: 22, 
                        borderRadius: 1 
                      }} 
                    />
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                      {moment(announcement.publishedAt || announcement.createdAt).format("MMM D, YYYY")}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>
                    {announcement.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.5
                    }}
                  >
                    {announcement.content}
                  </Typography>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            );
          })}
        </List>
      )}
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Button 
          fullWidth 
          onClick={onClose} 
          sx={{ 
            textTransform: "uppercase", 
            fontWeight: 700, 
            color: "text.secondary",
            letterSpacing: 0.5,
            "&:hover": { bgcolor: "action.hover" } 
          }}
        >
          БҮХ ЗАРЛАЛ ХАРАХ
        </Button>
      </Box>
    </Popover>
  );
};
